import { i18n } from 'boot/i18n';
import { Timestamp } from 'firebase/firestore';
import { isDate } from 'lodash';
import { useTime } from 'src/composables/time';

/**
 * Formats a duration object into a human-readable string.
 * @param {Object} param0 - The duration object.
 * @param {number} param0.years - The number of years.
 * @param {number} param0.days - The number of days.
 * @param {number} param0.hours - The number of hours.
 * @param {number} param0.minutes - The number of minutes.
 * @returns {string} The formatted duration string.
 */
const formatDurationInString = ({ years, days, hours, minutes }) => {
  let output = '';

  output += years ? `${years}y ` : '';
  output += days ? `${days}d ` : '';
  output += `${hours}h `;
  output += `${minutes}m`;

  return output;
}; // formatDurationInString()

/**
 * Calculates interval between two dates and returns the result in
 * duration output - '{xxxx}y {xx}d {xx}h {xx}m'.
 *
 * Also can format given days in duration output
 *
 * @param {Date} startDate
 * @param {Date} endDate
 * @param {number | null} intervalDays
 * @return {string | undefined}
 */
export const formatIntervalInDuration = (startDate, endDate, intervalDays = null) => {
  if (!intervalDays && (!isDate(startDate) || !isDate(endDate))) {
    return;
  }

  const intervalInMinutes = Math.floor(intervalDays
    ? (intervalDays * 24 * 60)
    : (endDate - startDate) / (1000 * 60));

  const output = {
    years: 0,
    days: 0,
    hours: 0,
    minutes: intervalInMinutes,
  };

  const daysInYear = 365;
  const hoursInDay = 24;
  const minutesInHour = 60;

  output.hours = Math.floor(output.minutes / minutesInHour);
  output.minutes %= minutesInHour;
  if (output.hours < hoursInDay) {
    return formatDurationInString(output);
  }

  output.days = Math.floor(output.hours / hoursInDay);
  output.hours %= hoursInDay;
  if (output.days < daysInYear) {
    return formatDurationInString(output);
  }

  output.years = Math.floor(output.days / daysInYear);
  output.days %= daysInYear;

  return formatDurationInString(output);
};

/**
 * @param {Date} dateToFormat
 * @return {string | undefined}
 */
export const formatDateFromNow = (dateToFormat) => {
  if (!isDate(dateToFormat)) {
    return;
  }

  const { formatDate, isSameDate, subtractFromDate } = useTime();
  const now = new Date();

  const isToday = isSameDate(dateToFormat, now, 'day');
  const isYesterday = isSameDate(dateToFormat, subtractFromDate(now, { days: 1 }), 'day');
  const isSameYear = isSameDate(dateToFormat, now, 'year');

  if (isToday) {
    return i18n.t('common.dates.today', { time: formatDate(dateToFormat, 'hh:mm a') });
  }

  if (isYesterday) {
    return i18n.t('common.dates.yesterday', { time: formatDate(dateToFormat, 'hh:mm a') });
  }

  if (isSameYear) {
    return formatDate(dateToFormat, 'dd MMM hh:mm a');
  }

  return formatDate(dateToFormat, 'dd MMM yyyy hh:mm a');
};

/**
 * Formats a specific appointment's own scheduled time in the active locale (e.g. "Tue, Sep 22 at
 * 9:00 AM" in English) - distinct from formatDateFromNow above, which is relative and describes
 * when something was posted/happened, not the time of the thing itself (HR-4744: a cancellation
 * notification's own timestamp reads as "when it was cancelled", which is misleading for a future
 * appointment unless the appointment's actual time is also shown, via this function).
 * @param {Date} dateToFormat
 * @param {boolean} [includeTime=true]
 * @return {string}
 */
export const formatAppointmentSchedule = (dateToFormat, includeTime = true) => {
  if (!isDate(dateToFormat)) {
    return '';
  }

  const { getLocaleDateString, getLocaleTimeString } = useTime();
  const locales = i18n.locale;

  const formattedDate = getLocaleDateString(dateToFormat, {
    locales,
    options: { weekday: 'short', month: 'short', day: 'numeric' },
  });

  if (!includeTime) {
    return formattedDate;
  }

  return i18n.t('common.dates.dateAtTime', {
    date: formattedDate,
    time: getLocaleTimeString(dateToFormat, { locales, options: { hour: 'numeric', minute: '2-digit' } }),
  });
};

/**
 * Calculates age from a Firebase Timestamp.
 *
 * @param {Timestamp} birthDate - Birth date as Firebase Timestamp.
 * @param {boolean} [format=true] - If true, returns a localized string like "26 years, 5 months".
 * If false, returns age in years as an integer number.
 *
 * @returns {string|number|null} Localized formatted age, numeric age in years, or null if input is invalid.
 */
export function calculateAge(birthDate, format = true) {
  if (!(birthDate instanceof Timestamp)) {
    return null;
  }

  const { startOfDate, getDateDiff } = useTime();

  const birth = startOfDate(birthDate, 'day');
  const now = startOfDate(new Date(), 'day');

  const diffYears = getDateDiff(now, birth, 'years');
  const diffMonths = getDateDiff(now, birth, 'months') % 12;

  const years = Math.trunc(diffYears || 0);

  if (!format) {
    return years;
  }

  const months = Math.trunc(diffMonths || 0);

  return `${
    i18n.tc('common.ageYears', years, { years })
  }, ${
    i18n.tc('common.ageMonths', months, { months })
  }`;
} // calculateAge()

/**
 * Whether a birth date falls on the same month and day as another date.
 *
 * Both sides are read in the viewer's zone, so this agrees with the date of birth that reader sees
 * on the profile: birthDate is stored at noon, so a reader twelve hours away reads the adjacent day
 * on both screens. A Feb 29 birth date rolls to Feb 28 in a common year.
 *
 * @param {Timestamp|null|undefined} birthDate - optional on a client
 * @param {Date|null|undefined} onDate - the date to test against
 * @returns {boolean}
 */
export function isBirthdayOn(birthDate, onDate) {
  if (!(birthDate instanceof Timestamp) || !(onDate instanceof Date)) {
    return false;
  }

  const birth = birthDate.toDate();

  if (Number.isNaN(birth.getTime()) || Number.isNaN(onDate.getTime())) {
    return false;
  }

  const { formatDate, adjustDate, daysInMonth } = useTime();

  const birthMonthDay = formatDate(birth, 'MM-dd');
  const targetMonthDay = formatDate(onDate, 'MM-dd');

  const bornOnLeapDay = birthMonthDay === '02-29';
  const februaryInTargetYear = adjustDate(onDate, { month: 2, day: 1 });
  const targetYearIsCommon = daysInMonth(februaryInTargetYear) === 28;

  // Gated on the target year, or a leap year would match both the 28th and the 29th.
  if (bornOnLeapDay && targetYearIsCommon) {
    return targetMonthDay === '02-28';
  }

  return birthMonthDay === targetMonthDay;
} // isBirthdayOn()

/**
 * Formats a calendar date — a 'yyyy-MM-dd' string — for display in the app's locale.
 *
 * A calendar date is a day on a wall calendar, not an instant, which is why this takes a string
 * rather than a Date or a Timestamp. `new Date('2026-07-01')` would parse it as UTC midnight and
 * render the day before for anyone west of UTC.
 *
 * Parsing it makes an instant regardless, so the day only survives if it is read back in the zone
 * it was parsed in. Both halves here go through `propToLibraryDate`, which is what keeps them on
 * the same zone: `extractDate` reads the string in the user's timezone and `getLocaleDateString`
 * renders through Luxon, in the zone the date already carries rather than the device's.
 *
 * Spelled-out month, because a coverage period is read as a fact rather than scanned in a column,
 * and '1/2/2026' is a different day either side of the Atlantic.
 *
 * @param {string|null|undefined} calendarDate - 'YYYY-MM-DD' (a longer ISO string is truncated)
 * @returns {string|null} the localized date, or null when there is nothing to format
 */
export function formatCalendarDate(calendarDate) {
  if (typeof calendarDate !== 'string' || calendarDate === '') {
    return null;
  }

  const { extractDate, getLocaleDateString } = useTime();
  const parsed = extractDate(calendarDate.slice(0, 10), 'yyyy-MM-dd');

  return Number.isNaN(parsed.getTime())
    ? null
    : getLocaleDateString(parsed, { options: { year: 'numeric', month: 'short', day: 'numeric' } });
} // formatCalendarDate()

/**
 * Parses a 'YYYY-MM-DD' calendar-date string (e.g. a router query param) into a Date, the same way
 * formatCalendarDate above does - it is read as midnight in the user's timezone rather than as UTC
 * midnight, and a malformed value is rejected outright rather than silently producing a nonsense
 * date (unlike Quasar's date.extractDate).
 * @param {unknown} calendarDate - expected to be a 'YYYY-MM-DD' string; anything else returns null
 * @returns {Date|null} the parsed date, or null when there is nothing valid to parse
 */
export function parseCalendarDateQueryParam(calendarDate) {
  if (typeof calendarDate !== 'string' || calendarDate === '') {
    return null;
  }

  const { extractDate } = useTime();
  const parsed = extractDate(calendarDate.slice(0, 10), 'yyyy-MM-dd');

  return Number.isNaN(parsed.getTime()) ? null : parsed;
} // parseCalendarDateQueryParam()
